import { useState, useEffect } from "react";
import { Button, Typography } from "@material-tailwind/react";
import { ArrowUpOnSquareIcon } from "@heroicons/react/24/outline";

// Detect iOS devices
const isIOS = () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

// Check if running in standalone mode (installed)
const isStandalone = () => {
    return window.matchMedia("(display-mode: standalone)").matches ||
        (window.navigator.standalone === true) ||
        document.referrer.includes('android-app://');
};

// Get browser language
const getBrowserLanguage = () => {
    return navigator.language || navigator.userLanguage || 'en';
};

// Get Safari button text in user's language
const getSafariButtonText = () => {
    const lang = getBrowserLanguage().toLowerCase();

    // Map of common languages to Safari button text
    const buttonTexts = {
        'en': 'Add to Home Screen',
        'sq': 'Shto në Ekranin Kryesor',
        'de': 'Zum Home-Bildschirm',
        'fr': 'Sur l\'écran d\'accueil',
        'es': 'Añadir a la pantalla de inicio',
        'it': 'Aggiungi alla schermata Home',
        'pt': 'Adicionar à tela inicial',
        'nl': 'Voeg toe aan beginscherm',
        'ru': 'На экран "Домой"',
        'tr': 'Ana Ekrana Ekle',
        'ar': 'إضافة إلى الشاشة الرئيسية',
        'zh': '添加到主屏幕',
        'ja': 'ホーム画面に追加',
        'ko': '홈 화면에 추가',
        'pl': 'Dodaj do ekranu głównego',
        'cs': 'Přidat na domovskou obrazovku',
        'sv': 'Lägg till på hemskärmen',
        'da': 'Tilføj til startskærm',
        'fi': 'Lisää kotinäytölle',
        'no': 'Legg til på hjemmeskjerm',
        'ro': 'Adaugă pe ecranul de start',
        'hu': 'Kezdőképernyőhöz adás',
        'el': 'Προσθήκη στην αρχική οθόνη',
        'he': 'הוסף למסך הבית',
        'th': 'เพิ่มไปที่หน้าจอหลัก',
        'vi': 'Thêm vào Màn hình chính',
        'id': 'Tambahkan ke Layar Utama',
        'ms': 'Tambah ke Skrin Utama',
    };

    // Check for exact match
    if (buttonTexts[lang]) {
        return buttonTexts[lang];
    }

    // Check for language code (e.g., 'en-US' -> 'en')
    const langCode = lang.split('-')[0];
    if (buttonTexts[langCode]) {
        return buttonTexts[langCode];
    }

    // Default to English
    return buttonTexts['en'];
};

const InstallPrompt = () => {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [showPrompt, setShowPrompt] = useState(false);
    const [isIOSDevice, setIsIOSDevice] = useState(false);

    useEffect(() => {
        const ios = isIOS();
        setIsIOSDevice(ios);

        // Check if app is already installed
        if (isStandalone()) {
            return; // App is already installed
        }

        // Handler for manual trigger from menu
        const handleShowPrompt = () => {
            setShowPrompt(true);
        };

        window.addEventListener('show-install-prompt', handleShowPrompt);

        let timer = null;

        // For iOS, show prompt after a delay (auto-show)
        if (ios) {
            // Check if user has dismissed the prompt before
            const hasDismissed = localStorage.getItem("pwa-install-dismissed");
            if (hasDismissed) {
                const dismissedDate = new Date(hasDismissed);
                const daysSinceDismissed = (new Date() - dismissedDate) / (1000 * 60 * 60 * 24);
                // Show again after 14 days
                if (daysSinceDismissed < 14) {
                    // Don't auto-show, but still listen for manual trigger
                    return () => {
                        window.removeEventListener('show-install-prompt', handleShowPrompt);
                    };
                }
            }

            // Show iOS prompt after a short delay
            timer = setTimeout(() => {
                setShowPrompt(true);
            }, 2000);
        }

        // For Android/Chrome, listen for the beforeinstallprompt event
        const handleBeforeInstallPrompt = (e) => {
            // Prevent the default browser install prompt
            e.preventDefault();
            // Store the event for later use
            setDeferredPrompt(e);

            // Check if user has dismissed the prompt before (using localStorage)
            const hasDismissed = localStorage.getItem("pwa-install-dismissed");
            if (hasDismissed) {
                const dismissedDate = new Date(hasDismissed);
                const daysSinceDismissed = (new Date() - dismissedDate) / (1000 * 60 * 60 * 24);
                // Show again after 14 days
                if (daysSinceDismissed < 14) {
                    return; // Don't show if dismissed within 14 days
                }
            }

            setShowPrompt(true);
        };

        // Only set up Android listeners if not iOS
        if (!ios) {
            window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
        }

        // Listen for app installed event
        const handleAppInstalled = () => {
            setShowPrompt(false);
            setDeferredPrompt(null);
        };

        window.addEventListener("appinstalled", handleAppInstalled);

        // Cleanup function
        return () => {
            if (timer) {
                clearTimeout(timer);
            }
            window.removeEventListener('show-install-prompt', handleShowPrompt);
            if (!ios) {
                window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
            }
            window.removeEventListener("appinstalled", handleAppInstalled);
        };
    }, []);

    const handleInstallClick = async () => {
        // For iOS, we can't programmatically trigger install
        // Just close the prompt - user needs to manually add to home screen
        if (isIOSDevice) {
            setShowPrompt(false);
            return;
        }

        if (!deferredPrompt) {
            return;
        }

        // Show the install prompt
        deferredPrompt.prompt();

        // Wait for the user to respond
        const { outcome } = await deferredPrompt.userChoice;

        if (outcome === "accepted") {
            console.log("User accepted the install prompt");
        } else {
            console.log("User dismissed the install prompt");
        }

        // Clear the deferred prompt
        setDeferredPrompt(null);
        setShowPrompt(false);
    };

    const handleDismiss = () => {
        setShowPrompt(false);
        // Store dismissal in localStorage for 14 days
        localStorage.setItem("pwa-install-dismissed", new Date().toISOString());
    };

    // For iOS, show prompt even without deferredPrompt
    // For Android, need both showPrompt and deferredPrompt
    if (!showPrompt || (!isIOSDevice && !deferredPrompt)) {
        return null;
    }

    return (
        <div className="fixed inset-0 flex items-center justify-center z-50 px-4 sm:px-0 bg-black/50">
            <div className="w-[calc(100%-2rem)] max-w-[350px] mx-auto bg-white rounded-xl border border-blue-gray-200 shadow-lg p-4 flex flex-col gap-3">
                <div className="flex justify-between items-start">
                    <Typography variant="h6" color="blue-gray" className="font-semibold">
                        Instalo aplikacionin
                    </Typography>
                    <button
                        onClick={handleDismiss}
                        className="text-blue-gray-500 hover:text-blue-gray-700"
                        aria-label="Mbyll"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={2}
                            stroke="currentColor"
                            className="h-5 w-5"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                </div>
                <Typography variant="small" color="blue-gray" className="text-left">
                    {isIOSDevice ? (
                        <>
                            Për të instaluar aplikacionin në iPhone/iPad:
                            <br />
                            <br />
                            <div className="mb-2">
                                1. Prekni butonin e ndarjes{" "}
                                <ArrowUpOnSquareIcon
                                    className="h-4 w-4 text-blue-gray-700 inline-block"
                                    style={{ verticalAlign: 'text-bottom', marginBottom: '1px' }}
                                />
                                {" "}në fund të ekranit
                            </div>
                            <div className="mb-2">
                                2. Zgjidhni <strong>"{getSafariButtonText()}"</strong>
                            </div>
                            <div>
                                3. Prekni butonin për të konfirmuar
                            </div>
                        </>
                    ) : (
                        "Instalo aplikacionin në telefonin tënd për të punuar pa internet dhe për qasje më të shpejtë"
                    )}
                </Typography>
                <div className="flex gap-2">
                    {!isIOSDevice && (
                        <Button
                            onClick={handleInstallClick}
                            color="blue"
                            size="md"
                            className="flex-1 normal-case"
                        >
                            Instalo
                        </Button>
                    )}
                    <Button
                        onClick={handleDismiss}
                        variant={isIOSDevice ? "filled" : "outlined"}
                        color={isIOSDevice ? "blue" : "blue-gray"}
                        size="md"
                        className={isIOSDevice ? "flex-1 normal-case" : "normal-case"}
                    >
                        Më vonë
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default InstallPrompt;
